/**
 * DeviceNotifications.jsx
 * The Settings card that turns on notifications for the device in your hand.
 *
 * This is the part that replaces ntfy: once it is on, Kreeboom itself can put
 * a notification on this computer or phone when a post fails, with the tab
 * closed. Each device is turned on separately, from that device.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { currentSubscription, permission, pushSupport, turnOff, turnOn } from '../utils/push.js';
import { Bell, Check, Warning } from './Icons.jsx';
import { formatLondon } from '../utils/time.js';

export default function DeviceNotifications() {
  const [server, setServer] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const support = pushSupport();

  async function refresh() {
    try {
      setServer(await api.notifications.pushStatus());
    } catch (e) {
      setServer({ error: e.message });
    }
    try {
      setSubscribed(Boolean(await currentSubscription()));
    } catch {
      setSubscribed(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function run(action, success) {
    setBusy(true);
    setNote(null);
    try {
      const result = await action();
      setNote({ ok: true, message: success ?? result?.message });
    } catch (e) {
      setNote({ ok: false, message: e.message });
    } finally {
      setBusy(false);
      refresh();
    }
  }

  const blocked = permission() === 'denied';
  const devices = server?.devices ?? [];

  return (
    <section className="card" id="notifications">
      <div className="card__head">
        <span className="tile">
          <Bell size={21} />
        </span>
        <div className="card__head-text">
          <h2>Notifications on this device</h2>
          <p>
            Kreeboom can tell you itself when a post fails or an account needs reconnecting, as a
            notification on this computer or phone, even with the app closed. No other app is
            needed. Everything also appears in the bell at the top of every page.
          </p>
        </div>
      </div>

      {!support.ok ? (
        <div className="notice notice--info">
          <Warning />
          <span>
            {support.reason === 'ios-needs-install'
              ? 'On an iPhone or iPad this needs Kreeboom on your Home Screen. In Safari, tap Share, then Add to Home Screen, open Kreeboom from there, and come back to this page.'
              : 'This browser cannot show notifications from a website. Chrome, Edge, Firefox and Safari can.'}
          </span>
        </div>
      ) : server?.configured === false ? (
        <div className="notice notice--info">
          <Warning />
          <span>Notifications are not set up on the server yet.</span>
        </div>
      ) : (
        <>
          <div className="row">
            <div>
              <div className="row__label">
                {subscribed ? 'On for this device' : 'Off for this device'}
              </div>
              <div className="row__sub">
                {blocked
                  ? 'Blocked in this browser. Allow notifications in the site settings (the icon left of the address), then turn this on.'
                  : subscribed
                    ? 'You will be notified here, with the app closed.'
                    : 'Your browser will ask for permission when you turn it on.'}
              </div>
            </div>
            <div className="row__end">
              {subscribed ? (
                <button className="btn btn--sm" onClick={() => run(turnOff, 'Turned off for this device.')} disabled={busy}>
                  Turn off
                </button>
              ) : (
                <button
                  className="btn btn--sm btn--primary"
                  onClick={() =>
                    run(turnOn, 'On for this device. Send a test to make sure they arrive.')
                  }
                  disabled={busy}
                >
                  <Bell size={15} /> Turn on
                </button>
              )}
            </div>
          </div>

          <div className="btn-row" style={{ marginTop: 12 }}>
            <button
              className="btn"
              onClick={() => run(() => api.notifications.pushTest())}
              disabled={busy || devices.length === 0}
            >
              {busy ? 'Working…' : 'Send a test'}
            </button>
          </div>

          {devices.length > 0 && (
            <p className="hint">
              On for {devices.length} device{devices.length === 1 ? '' : 's'}:{' '}
              {devices
                .map(
                  (d) =>
                    `${d.label || 'unnamed device'}${d.last_sent_at ? ` (last reached ${formatLondon(d.last_sent_at)})` : ''}`,
                )
                .join(', ')}
              . Turn it on from each device you want notified.
            </p>
          )}
        </>
      )}

      {note && (
        <div className={`notice notice--${note.ok ? 'info' : 'error'}`}>
          {note.ok ? <Check /> : <Warning />}
          <span>{note.message}</span>
        </div>
      )}
    </section>
  );
}
